import { Component, OnInit } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { finalize } from "rxjs";
import {
  Organization,
  BusinessUnit,
  AssetGroup,
  Plant,
} from "../../interfaces/asset-hierarchy/asset-hierarchy.models";
import {
  LkpCountry,
  LkpCvxCriticalityRanking,
} from "../../interfaces/turbines/lookup.interface";
import { Turbine } from "../../interfaces/turbines/turbine.interface";
import { AssetHierarchyService } from "../../services/asset-hierarchy.service";
import { TurbineService } from "../../services/turbine.service";
import { UserDto } from "../../interfaces/graph/user.interface";

@Component({
    selector: "app-turbine-create",
    templateUrl: "./turbine-create.component.html",
    styleUrls: ["./turbine-create.component.css"],
    standalone: false
})
export class TurbineCreateComponent implements OnInit {
  turbineForm: FormGroup;
  loading = false;
  saving = false;
  error: string | null = null;
  validationErrors: string[] = [];
  // Asset Hierarchy data
  organizations: Organization[] = [];
  businessUnits: BusinessUnit[] = [];
  assetGroups: AssetGroup[] = [];
  plants: Plant[] = [];
  // Lookup data from API
  countries: LkpCountry[] = [];
  cvxCriticalityRankings: LkpCvxCriticalityRanking[] = [];
  cvxCriticalityRankingOptions: LkpCvxCriticalityRanking[] = [];
  oemCriticalityRankingOptions: LkpCvxCriticalityRanking[] = [];
  // Dropdown options
  gtOemOptions = [
    { value: "ALSTOM", label: "ALSTOM" },
    { value: "GE", label: "GE" },
    { value: "ROLLS ROYCE", label: "ROLLS ROYCE" },
    { value: "SIEMENS", label: "SIEMENS" },
    { value: "SOLAR", label: "SOLAR" },
  ];
  drivenEquipOptions = [
    { value: "Compressor", label: "Compressor" },
    { value: "Generator", label: "Generator" },
    { value: "Pump", label: "Pump" },
  ];

  fuelOptions = [
    { value: "Gas", label: "Gas" },
    { value: "Steam", label: "Steam" },
    { value: "Wind", label: "Wind" },
    { value: "Liquid", label: "Liquid" },
  ];

  cycleHzOptions = [
    { value: "50", label: "50Hz" },
    { value: "60", label: "60Hz" },
  ];

  boilTypeOptions = [
    { value: "NA", label: "NA" },
    { value: "EOR", label: "EOR" },
    { value: "Superheated", label: "Superheated" },
  ];

  installationTypeOptions = [
    { value: "MECHANICAL DRIVE", label: "MECHANICAL DRIVE" },
    { value: "POWER GENERATION", label: "POWER GENERATION" },
    { value: "NOT YET RUNNING", label: "NOT YET RUNNING" },
    { value: "OUT OF SERVICE", label: "OUT OF SERVICE" },
    { value: "UNKNOWN", label: "UNKNOWN" },
    { value: "SPARE", label: "SPARE" },
  ];

  serviceTypeOptions = [
    { value: "Heat_Recovery", label: "Heat Recovery" },
    { value: "Simple_Cycle", label: "Simple Cycle" },
  ];

  oemSupportOptions = [{ value: "LTSA", label: "LTSA" }];

  oemSupportRelationshipOptions = [
    { value: "LTSA", label: "LTSA" },
    { value: "Service_Agreement", label: "Service Agreement" },
    { value: "Parts_Only", label: "Parts Only" },
    { value: "None", label: "None" },
  ];

  constructor(
    private fb: FormBuilder,
    private turbineService: TurbineService,
    private assetHierarchyService: AssetHierarchyService,
    private router: Router
  ) {
    this.turbineForm = this.createForm();
  }
  ngOnInit(): void {
    this.loadOrganizations();
    this.loadLookupData();
    this.setupFormSubscriptions();
  }

  private createForm(): FormGroup {
    const form = this.fb.group({
      // Asset Hierarchy
      organizationId: ["", Validators.required],
      businessUnitId: [{value: "", disabled: true}, Validators.required],
      assetGroupId: [{value: "", disabled: true}, Validators.required],
      plantId: [{value: "", disabled: true}, Validators.required],

      // Turbine Details
      serialNumber: ["", Validators.required],
      unitShortName: [""],
      chevronIdentifier: [""],
      gtOem: [""],
      drivenEquip: [""],
      drivenEquipOem: [""],
      frameType: [""],
      equipCode: [""],
      cvxCriticalityRanking: [""],
      oemCriticalityRanking: [""],
      installYear: ["", [Validators.min(1900), Validators.max(2050)]],
      firstFireDate: [""],
      capacity: [""],
      rpm: [""],
      cycleHz: [""],
      primaryFuel: [""],
      altFuel1: [""],
      altFuel2: [""],
      gtControlSystem: [""],
      excitationSystem: [""],
      combustionSystem: [""],
      boilType: [""],
      installationType: [""],
      serviceHours: [""],
      latitude: [""],
      longitude: [""],
      country: [""],
      serviceType: [""],
      offshore: [""],
      statusDescription: [""],
      turbineStatus: [""],
      nextPlannedOutage: [""],
      lastPlannedOutage: [""],
      baseloadFiringTemperature: [""],
      designHeatRate: [""],
      oemSiteAccountManager: [""],
      oemSiteAccountManagerEmail: ["", Validators.email],
      oemCorporateAccountManager: [""],
      oemCorporateAccountManagerEmail: ["", Validators.email],
      cvxCorporateContact: [""],
      cvxCorporateContactEmail: ["", Validators.email],
      cvxSiteContact: [""],
      cvxSiteContactEmail: ["", Validators.email],
      oemSupportRelationship: [""],
      endServiceDate: [""],

      // RAM Targets
      availabilityPercentTargetGreen: [
        "",
        [Validators.min(0), Validators.max(100)],
      ],
      availabilityPercentTargetYellow: [
        "",
        [Validators.min(0), Validators.max(100)],
      ],
      reliabilityPercentTargetGreen: [
        "",
        [Validators.min(0), Validators.max(100)],
      ],
      reliabilityPercentTargetYellow: [
        "",
        [Validators.min(0), Validators.max(100)],
      ], // Monitoring Controls
      pscMscMonitored: [false],
      localDscMonitored: [false],
      impactManaged: [false],
      orapReported: [false],
      oemMonitored: [false],

      // Equipment ID fields
      cuprEquipmentId: [""],
      eamMmsEquipmentId: [""],
      topsId: [""],
    });
    
    return form;
  }
  private setupFormSubscriptions(): void {
    // Organization change
    this.turbineForm.get("organizationId")?.valueChanges.subscribe((orgId) => {
      if (orgId) {
        // Clear dependent dropdowns
        this.turbineForm.patchValue({
          businessUnitId: "",
          assetGroupId: "",
          plantId: "",
        });
        this.businessUnits = [];
        this.assetGroups = [];
        this.plants = [];

        // Disable dependent controls
        this.turbineForm.get('businessUnitId')?.disable();
        this.turbineForm.get('assetGroupId')?.disable();
        this.turbineForm.get('plantId')?.disable();

        // Load business units (which may auto-select)
        this.loadBusinessUnits(orgId);
      } else {
        // Clear and disable dependent controls when organization is not selected
        this.businessUnits = [];
        this.assetGroups = [];
        this.plants = [];
        this.turbineForm.get('businessUnitId')?.disable();
        this.turbineForm.get('assetGroupId')?.disable();
        this.turbineForm.get('plantId')?.disable();
      }
    });

    // Business Unit change
    this.turbineForm.get("businessUnitId")?.valueChanges.subscribe((buId) => {
      if (buId) {
        // Clear dependent dropdowns
        this.turbineForm.patchValue({
          assetGroupId: "",
          plantId: "",
        });
        this.assetGroups = [];
        this.plants = [];

        // Disable dependent controls
        this.turbineForm.get('assetGroupId')?.disable();
        this.turbineForm.get('plantId')?.disable();

        // Load asset groups (which may auto-select)
        this.loadAssetGroups(buId);
      }
    });

    // Asset Group change
    this.turbineForm.get("assetGroupId")?.valueChanges.subscribe((agId) => {
      if (agId) {
        // Clear dependent dropdown
        this.turbineForm.patchValue({
          plantId: "",
        });
        this.plants = [];

        // Disable dependent control
        this.turbineForm.get('plantId')?.disable();

        // Load plants (which may auto-select)
        this.loadPlants(agId);
      }
    });
  }
  private loadOrganizations(): void {
    this.loading = true;
    this.error = null;

    this.assetHierarchyService
      .getOrganizations()
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (orgs) => {
          this.organizations = orgs;

          if (!orgs || orgs.length === 0) {
            this.error =
              "No organizations found. Please contact your administrator.";
          } else {
            // Auto-select first organization
            this.turbineForm.patchValue({ organizationId: orgs[0].id });
          }
        },
        error: (error) => {
          this.error = "Failed to load organizations. Please try again.";
          console.error("Error loading organizations:", error);
        },
      });
  }
  private loadBusinessUnits(organizationId: number): void {
    this.assetHierarchyService
      .getBusinessUnitsByOrganizationId(organizationId)
      .subscribe({
        next: (businessUnits) => {
          this.businessUnits = businessUnits;

          // Enable and auto-select first business unit
          this.turbineForm.get('businessUnitId')?.enable();
          if (businessUnits.length > 0) {
            this.turbineForm.patchValue({
              businessUnitId: businessUnits[0].id,
            });
          }
        },
        error: (error) => {
          this.error = "Failed to load business units. Please try again.";
          console.error("Error loading business units:", error);
        },
      });
  }
  private loadAssetGroups(businessUnitId: number): void {
    this.assetHierarchyService
      .getAssetGroupsByBusinessUnitId(businessUnitId)
      .subscribe({
        next: (assetGroups) => {
          this.assetGroups = assetGroups;

          // Enable and auto-select first asset group
          this.turbineForm.get('assetGroupId')?.enable();
          if (assetGroups.length > 0) {
            this.turbineForm.patchValue({ assetGroupId: assetGroups[0].id });
          }
        },
        error: (error) => {
          this.error = "Failed to load asset groups. Please try again.";
          console.error("Error loading asset groups:", error);
        },
      });
  }
  private loadPlants(assetGroupId: number): void {
    this.assetHierarchyService.getPlantsByAssetGroupId(assetGroupId).subscribe({
      next: (plants) => {
        this.plants = plants;

        // Enable and auto-select first plant
        this.turbineForm.get('plantId')?.enable();
        if (plants.length > 0) {
          this.turbineForm.patchValue({ plantId: plants[0].id });
        }
      },
      error: (error) => {
        this.error = "Failed to load plants. Please try again.";
        console.error("Error loading plants:", error);
      },
    });
  }

  private loadLookupData(): void {
    // Load countries
    this.turbineService.getCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
      },
      error: (error) => {
        console.error("Error loading countries:", error);
        // Don't show error to user as this is optional data
      },
    });

    // Load CVX criticality rankings
    this.turbineService.getCvxCriticalityRankings().subscribe({
      next: (rankings) => {
        this.cvxCriticalityRankings = rankings;
        this.cvxCriticalityRankingOptions = rankings;
        this.oemCriticalityRankingOptions = rankings;
      },
      error: (error) => {
        console.error("Error loading CVX criticality rankings:", error);
        // Don't show error to user as this is optional data
      },
    });
  }

  private formatDateForApi(dateValue: any): string | undefined {
    if (!dateValue) {
      return undefined;
    }

    // If it's already a string in YYYY-MM-DD format, return as is
    if (
      typeof dateValue === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
    ) {
      return dateValue;
    }

    // Convert Date object or other format to YYYY-MM-DD
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return undefined;
    }

    return date.toISOString().split("T")[0];
  }

  onSubmit(): void {
    // Clear previous validation errors
    this.validationErrors = [];
    this.error = null;

    // Check form validity and collect validation errors
    if (!this.turbineForm.valid) {
      this.collectValidationErrors();
      this.markFormGroupTouched();
      return;
    }

    this.saving = true;
    const formValue = this.turbineForm.getRawValue();
      const turbine: Turbine = {
        id: 0,
        plantId: formValue.plantId,
        country: formValue.country,
        equipCode: formValue.equipCode,
        serialNumber: formValue.serialNumber,
        unitShortName: formValue.unitShortName,
        drivenEquip: formValue.drivenEquip,
        oemCriticalityRanking: formValue.oemCriticalityRanking,
        cvxCriticalityRanking: formValue.cvxCriticalityRanking,
        capacity: formValue.capacity || undefined,
        firstFireDate: this.formatDateForApi(formValue.firstFireDate),
        power: undefined, // Not in form but exists in DTO
        rpm: formValue.rpm || undefined,
        cycleHz: formValue.cycleHz || undefined,
        primaryFuel: formValue.primaryFuel,
        altFuel1: formValue.altFuel1,
        altFuel2: formValue.altFuel2,
        combustionSystem: formValue.combustionSystem,
        drivenEquipOem: formValue.drivenEquipOem,
        oemSupportRelationship: formValue.oemSupportRelationship,
        boilType: formValue.boilType,
        installationType: formValue.installationType,
        serviceType: formValue.serviceType,
        pitagCurrentOutput: undefined, // Not in form but exists in DTO
        piServerName: undefined, // Not in form but exists in DTO
        latitude: formValue.latitude,
        longitude: formValue.longitude,
        serviceHours: formValue.serviceHours || undefined,
        pscMscMonitored: formValue.pscMscMonitored,
        oemMonitored: formValue.oemMonitored,
        localdscMonitored: formValue.localDscMonitored,
        impactManaged: formValue.impactManaged,
        orapReported: formValue.orapReported,
        chevronIdentifier: formValue.chevronIdentifier,
        designHeatrate: formValue.designHeatRate || undefined, // Ensure it's a string or undefined
        endServiceDate: this.formatDateForApi(formValue.endServiceDate),
        oemSiteAccountManagerEmail: formValue.oemSiteAccountManagerEmail,
        oemCorporateAccountManager: formValue.oemCorporateAccountManager,
        oemCorporateAccountManagerEmail:
          formValue.oemCorporateAccountManagerEmail,
        oemSiteAccountManager: formValue.oemSiteAccountManager,
        cvxCorporateContact: formValue.cvxCorporateContact,
        cvxCorporateContactEmail: formValue.cvxCorporateContactEmail,
        cvxSiteContact: formValue.cvxSiteContact,
        cvxSiteContactEmail: formValue.cvxSiteContactEmail,
        availabilityPercentTargetGreen:
          formValue.availabilityPercentTargetGreen || undefined,
        availabilityPercentTargetYellow:
          formValue.availabilityPercentTargetYellow || undefined,
        reliabilityPercentTargetGreen:
          formValue.reliabilityPercentTargetGreen || undefined,
        reliabilityPercentTargetYellow:
          formValue.reliabilityPercentTargetYellow || undefined,
        gtOem: formValue.gtOem,
        rowCreatedDate: undefined, // Set by backend
        rowUpdatedDate: undefined, // Set by backend
        gtControlSystem: formValue.gtControlSystem,
        excitationSystem: formValue.excitationSystem,
        baseloadFiringTemperature:
          formValue.baseloadFiringTemperature || undefined,
        installYear: formValue.installYear || undefined,
        rowUpdatedBy: undefined, // Set by backend
        rowCreatedBy: undefined, // Set by backend
        nextPlannedOutage: this.formatDateForApi(formValue.nextPlannedOutage),
        lastPlannedOutage: this.formatDateForApi(formValue.lastPlannedOutage),
        currentOutput: undefined, // Not in form but exists in DTO
        drivenEquipOEM: formValue.drivenEquipOEM,
        frameType: formValue.frameType,
        turbineStatus: formValue.turbineStatus,
        statusDescription: formValue.statusDescription,
        offshore: formValue.offshore,
        cuprEquipmentId: formValue.cuprEquipmentId || "",
        eamMmsEquipmentId: formValue.eamMmsEquipmentId || "",
        topsId: formValue.topsId || "",
      };

      this.turbineService
        .createTurbine(turbine)
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: (createdTurbine) => {
            this.router.navigate(["/turbines/edit", createdTurbine.id]);
          },
          error: (error) => {
            this.error = "Failed to create turbine. Please try again.";
            console.error("Error creating turbine:", error);
          },
        });
  }

  onCancel(): void {
    this.router.navigate(["/turbines"]);
  }

  private collectValidationErrors(): void {
    const errors: string[] = [];
    
    Object.keys(this.turbineForm.controls).forEach((key) => {
      const control = this.turbineForm.get(key);
      if (control && control.invalid) {
        const fieldDisplayName = this.getFieldDisplayName(key);
        if (control.errors?.['required']) {
          errors.push(`${fieldDisplayName} is required`);
        }
        if (control.errors?.['email']) {
          errors.push(`${fieldDisplayName} must be a valid email address`);
        }
        if (control.errors?.['min']) {
          errors.push(`${fieldDisplayName} must be at least ${control.errors['min'].min}`);
        }
        if (control.errors?.['max']) {
          errors.push(`${fieldDisplayName} must be at most ${control.errors['max'].max}`);
        }
      }
    });
    
    this.validationErrors = errors;
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      organizationId: 'Organization',
      businessUnitId: 'Business Unit',
      assetGroupId: 'Asset Group',
      plantId: 'Plant',
      serialNumber: 'Serial Number',
      unitShortName: 'Unit Short Name',
      chevronIdentifier: 'Chevron Unit Name',
      gtOem: 'GT OEM',
      drivenEquip: 'Driven Equipment',
      drivenEquipOem: 'Driven Equipment OEM',
      frameType: 'Frame Type',
      equipCode: 'Equipment Code',
      cvxCriticalityRanking: 'CVX Criticality Ranking',
      oemCriticalityRanking: 'OEM Criticality Ranking',
      installYear: 'Install Year',
      firstFireDate: 'First Fire Date',
      capacity: 'Capacity',
      rpm: 'RPM',
      cycleHz: 'Cycle Hz',
      primaryFuel: 'Primary Fuel',
      altFuel1: 'Alternative Fuel 1',
      altFuel2: 'Alternative Fuel 2',
      gtControlSystem: 'GT Control System',
      excitationSystem: 'Excitation System',
      combustionSystem: 'Combustion System',
      boilType: 'Boil Type',
      installationType: 'Installation Type',
      serviceHours: 'Service Hours',
      latitude: 'Latitude',
      longitude: 'Longitude',
      country: 'Country',
      serviceType: 'Service Type',
      offshore: 'Offshore',
      statusDescription: 'Status Description',
      turbineStatus: 'Turbine Status',
      nextPlannedOutage: 'Next Planned Outage',
      lastPlannedOutage: 'Last Planned Outage',
      baseloadFiringTemperature: 'Baseload Firing Temperature',
      designHeatRate: 'Design Heat Rate',
      oemSiteAccountManager: 'OEM Site Account Manager',
      oemSiteAccountManagerEmail: 'OEM Site Account Manager Email',
      oemCorporateAccountManager: 'OEM Corporate Account Manager',
      oemCorporateAccountManagerEmail: 'OEM Corporate Account Manager Email',
      cvxCorporateContact: 'CVX Corporate Contact',
      cvxCorporateContactEmail: 'CVX Corporate Contact Email',
      cvxSiteContact: 'CVX Site Contact',
      cvxSiteContactEmail: 'CVX Site Contact Email',
      oemSupportRelationship: 'OEM Support Relationship',
      endServiceDate: 'End Service Date',
      availabilityPercentTargetGreen: 'Availability Percent Target Green',
      availabilityPercentTargetYellow: 'Availability Percent Target Yellow',
      reliabilityPercentTargetGreen: 'Reliability Percent Target Green',
      reliabilityPercentTargetYellow: 'Reliability Percent Target Yellow',
      cuprEquipmentId: 'CUPR Equipment ID',
      eamMmsEquipmentId: 'EAM MMS Equipment ID',
      topsId: 'TOPS ID'
    };
    
    return displayNames[fieldName] || fieldName;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.turbineForm.controls).forEach((key) => {
      const control = this.turbineForm.get(key);
      control?.markAsTouched();
    });
  }

  // Helper methods for validation
  isFieldInvalid(fieldName: string): boolean {
    const field = this.turbineForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.turbineForm.get(fieldName);
    if (field && field.errors) {
      if (field.errors["required"]) {
        return `${fieldName} is required`;
      }
      if (field.errors["email"]) {
        return "Please enter a valid email address";
      }
      if (field.errors["min"]) {
        return `Value must be at least ${field.errors["min"].min}`;
      }
      if (field.errors["max"]) {
        return `Value must be at most ${field.errors["max"].max}`;
      }
    }
    return "";
  }

  onCvxCorporateContactSelected(user: UserDto | null): void {
    if (user) {
      this.turbineForm.patchValue({
        cvxCorporateContact: `${user.firstName} ${user.lastName}`,
        cvxCorporateContactEmail: user.email
      });
    } else {
      this.turbineForm.patchValue({
        cvxCorporateContact: '',
        cvxCorporateContactEmail: ''
      });
    }
  }

  onCvxCorporateContactEmailSelected(user: UserDto | null): void {
    if (user) {
      this.turbineForm.patchValue({
        cvxCorporateContact: `${user.firstName} ${user.lastName}`,
        cvxCorporateContactEmail: user.email
      });
    } else {
      this.turbineForm.patchValue({
        cvxCorporateContact: '',
        cvxCorporateContactEmail: ''
      });
    }
  }

  onCvxSiteContactSelected(user: UserDto | null): void {
    if (user) {
      this.turbineForm.patchValue({
        cvxSiteContact: `${user.firstName} ${user.lastName}`,
        cvxSiteContactEmail: user.email
      });
    } else {
      this.turbineForm.patchValue({
        cvxSiteContact: '',
        cvxSiteContactEmail: ''
      });
    }
  }

  onCvxSiteContactEmailSelected(user: UserDto | null): void {
    if (user) {
      this.turbineForm.patchValue({
        cvxSiteContact: `${user.firstName} ${user.lastName}`,
        cvxSiteContactEmail: user.email
      });
    } else {
      this.turbineForm.patchValue({
        cvxSiteContact: '',
        cvxSiteContactEmail: ''
      });
    }
  }
}
